import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { BorderColorOutlined } from '@mui/icons-material';
import { CustomButton, CustomChip } from '@components';
import { COLORS } from '@styles';
import { InvitationStatus, OrganizationStatus } from '@graphql';
import { paths } from '@utils';

interface IProps {
  client: any;
  searchedEmail: string;
  onEdit: () => void;
}

const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase();

const FIELDS: Array<{ label: string; value: (client: any) => React.ReactNode }> = [
  {
    label: 'ID',
    value: (client) => <Typography sx={{ fontFamily: 'monospace' }}>{client.id}</Typography>,
  },
  { label: 'First name', value: (client) => client.firstName },
  { label: 'Last name', value: (client) => client.lastName },
  {
    label: 'Phone',
    value: (client) =>
      client.phone || <Typography sx={{ color: COLORS.NEUTRAL_LIGHT_0 }}>Not provided</Typography>,
  },
  { label: 'Role', value: (client) => client.role },
  { label: 'Invitation status', value: (client) => client.invitationStatus },
  { label: 'Onboarding status', value: (client) => client.onboardingStatus },
];

const makeStyles = () => ({
  card: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    color: COLORS.NEUTRAL_WHITE,
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1.5rem 2rem',
    borderBottom: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    backgroundColor: COLORS.NEUTRAL_DARK_2,
  },
  headerTags: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.75rem',
  },
  orgLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.25rem',
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  body: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '1.75rem 2rem',
    padding: '1.75rem 2rem 2rem',
  },
  fieldLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: COLORS.NEUTRAL_LIGHT_0,
    fontWeight: 600,
    fontSize: '0.8125rem',
  },
});

export function LookupResultCard({ client, searchedEmail, onEdit }: IProps) {
  const sx = makeStyles();

  const matchedSignIn = normalize(client.email) === normalize(searchedEmail);
  const matchedWork = !!client.workEmail && normalize(client.workEmail) === normalize(searchedEmail);

  return (
    <Box sx={sx.card}>
      <Box sx={sx.header}>
        <Box>
          <Box sx={sx.headerTags}>
            <Typography variant="h6">{`${client.firstName} ${client.lastName}`}</Typography>
            <CustomChip variant="default" label={client.role} small />
            {client.invitationStatus === InvitationStatus.Pending && (
              <CustomChip variant="warning" label="Invite pending" small />
            )}
          </Box>
          <Box sx={sx.orgLine}>
            <Typography variant="body2">
              Organization:{' '}
              <Link component={RouterLink} to={`${paths.organizations}/${client.org.id}`}>
                {client.org.name}
              </Link>
            </Typography>
            {client.org.status === OrganizationStatus.Inactive && (
              <CustomChip variant="dark" label="Inactive" small />
            )}
          </Box>
        </Box>
        <CustomButton variant="default" width="auto" icon={<BorderColorOutlined />} onClick={onEdit}>
          Edit
        </CustomButton>
      </Box>

      <Box sx={sx.body}>
        <Box>
          <Typography component="div" sx={sx.fieldLabel}>
            Email (sign-in)
            {matchedSignIn && <CustomChip variant="success" label="Matched" small />}
          </Typography>
          <Typography>{client.email}</Typography>
        </Box>
        <Box>
          <Typography component="div" sx={sx.fieldLabel}>
            Work email
            {matchedWork && <CustomChip variant="success" label="Matched" small />}
          </Typography>
          <Typography>{client.workEmail}</Typography>
        </Box>
        {FIELDS.map(({ label, value }) => (
          <Box key={label}>
            <Typography sx={sx.fieldLabel}>{label}</Typography>
            <Typography component="div">{value(client)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
